import React, { useState, Fragment} from "react";
import { Grid, Tooltip, Checkbox, Chip, Dialog, DialogContent, DialogContentText, TextField, IconButton, Button, Select, DialogActions  } from '@material-ui/core';
import { Delete, HelpOutline, CheckBoxOutlineBlank, CheckBox, SettingsApplications } from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const availableConditions = [
  { field: "duration", operations:["greaterThan", "lessThan"], explain: "Applies this rule to meetings where duration is greater than, or less than." },
  { field: "invited", operations:["greaterThan", "lessThan"], explain: "Applies this rule to meetings where total invited count is greater than, or less than. This number includes organizer." },
  { field: "invitedRequired", operations:["greaterThan", "lessThan"], explain: "Applies this rule to meetings where required invited is greater than, or less than. This number excludes organizer." },
  { field: "cost.time", operations:["greaterThan", "lessThan"], explain: "Applies this rule to meetings where total time (participants * duration) is greater than, or less than." },
  { field: "cost.time90", operations:["greaterThan", "lessThan"], explain: "Applies this rule to meetings where recurring total time is (participants * duration * recurrence in 90 days) is greater than, or less than." },
  { field: "cost.externalRate", operations:["greaterThan", "lessThan"], explain: "Applies this rule to meetings where the external participant count is greater than, or less than." },
  { field: "sensitivity", operations:["match"], options:["normal", "private", "personal", "confidential"], explain: "Applies to meetings where sensitivity label is normal, private, personal, or confidential (Available only in Outlook)" },
  { field: "organizer.emailAddress", operations:["contains", "match"], explain: "Meeting is hosted by an authorized user. Please consider changing the organizer." },
]
const availableFields = availableConditions.map(c => c.field);
const operationTypes = {
  "greaterThan": "number",
  "lessThan": "number",
  "equal": "text",
  "notEqual": "text",
  "contains": "text",
  "match": "array",
}

const ConditionEditor = ({ id, name, conditions, disabled, updateConditions }) => {

  const [conditionDialog, setConditionDialog] = useState({conditions: conditions ? conditions : [], open:false, changed:false});

    // Add, Delete, Apply, Open and Close Dialog methods
  const openDialog = () => setConditionDialog({conditions: conditions ? conditions : [], open:true, changed:false})
  const closeDialog = () => setConditionDialog({conditions: conditions ? conditions : [], open:false, changed:false});
  const onDeleteClick = (index) => {
    conditionDialog.conditions = conditionDialog.conditions.filter((_item, i) => index !== i);
    setConditionDialog({...conditionDialog, changed:true});
  }
  const onAddClick = () => {
    conditionDialog.conditions.push({field:availableFields[0]});
    setConditionDialog({...conditionDialog, changed:true});
  }
  const onApplyClick = () => {
    if(conditionDialog.conditions && conditionDialog.conditions.length > 0)
      updateConditions(conditionDialog.conditions);
    else
      updateConditions([]);
    closeDialog();
  };

  const updateCondition = (index, variable, value) => {
    // Reset the operation and value in case we change field
    if(variable === "field")
      conditionDialog.conditions[index]= {field: value};
    else
      conditionDialog.conditions[index] = {field: conditionDialog.conditions[index].field, [variable]:value};
    setConditionDialog({...conditionDialog, changed:true});
  }



  const conditionFieldPicker = (value, index) => {
    const empty = <option disabled key={"empty-field-picker-" + index} value="">Select Field</option>
    const selectOptions = [empty].concat(availableFields.map(o => <option key={"field-"+o} value={o}>{o}</option>));
    const onChange = (e) => updateCondition(index, "field", e.currentTarget.value);
    return <Select native onChange={onChange} value={value ? value : ""}>{selectOptions}</Select>
  }

  const conditionOperatorPicker = (field, operation, index) => {
    if(!field)  
      return <Select native disabled value={"Unavailable"}></Select>
    const availableOperations = availableConditions.find(c => c.field === field).operations;
    const empty = <option disabled  key={"empty-operation-picker-" + index}  value="">Select Operation</option>
    const selectOptions = [empty].concat(availableOperations.map(o => <option key={"field-"+o} value={o}>{o}</option>))
    
    const onChange = (e) => updateCondition(index, e.currentTarget.value, "");
    return <Select native displayEmpty onChange={onChange} value={operation ? operation : ""} >{selectOptions}</Select>
  }

  const conditionValueEditor = (field, operation, value, index) => {
    const type = operationTypes[operation];
    const condition = availableConditions.find(c => c.field === field);
    if(type === "text") {
      const onChangeText = (e) => updateCondition(index, operation, e.currentTarget.value)
      return <TextField value={value ? value : ""} onChange={onChangeText} type="text" fullWidth variant="outlined" key={"value-editor-"+index} />
    }
    if(type === "number"){
      const onChangeNumber = (e) => updateCondition(index, operation, parseInt(e.currentTarget.value, 10))
      return <TextField value={value ? value : 1} onChange={onChangeNumber} type="number" variant="outlined" InputLabelProps={{shrink: true}} inputProps={{min:0}} key={"value-editor-"+index} />
    }

    if(type === "array"){
      const selectedOptions = value ? value : [];
      const onChangeOptions = (_event, value) => updateCondition(index, operation, value);

      if(!condition.options){
        const chipRender = (value, props) => value.map((option, index) => ( <Chip label={option} {...props({ index })} /> ));
        const chipRenderInput = (params) => <TextField placeholder={value && value.length ? "" : "Type + Press Enter" } {...params} />;
        return <Autocomplete disableClearable value={selectedOptions} options={[]} onChange={onChangeOptions} freeSolo multiple renderTags={ chipRender } renderInput={ chipRenderInput } key={"value-editor-"+index} />
      }
      
      if(condition.options){
        const optionRender = (option, { selected }) => <Fragment> <Checkbox icon={icon} checkedIcon={checkedIcon} className="m-1" checked={selected} /> {option} </Fragment>;
        const optionRenderInput = (params) => <TextField {...params} placeholder={value && value.length ? "" : "Click to select" } />;
        return <Autocomplete multiple disableClearable value={selectedOptions} onChange={onChangeOptions} options={condition.options} disableCloseOnSelect renderOption={optionRender} renderInput={optionRenderInput} key={"value-editor-"+index} />
      }
    }
  }

  const conditionRowRender = (condition, index) => {
    const {field, ...ops} = condition;
    const [operation, value] = Object.entries(ops).length ? Object.entries(ops)[0] : [null, null];
    const tooltip = availableConditions.find(c => c.field === field).explain;
    return (
      <Grid container spacing={2} direction="row" alignItems="center" key={"condition-item-row-"+index}>
        <Grid item xs={1}>
          <IconButton id={"condition-item-icon-button-"+index} onClick={() => onDeleteClick(index)} aria-label="Delete this item">
            <Delete id={"condition-item-delete-"+index}/>
          </IconButton>
        </Grid>
        <Grid item xs={1}>
          <Tooltip title={tooltip} className="mr-2">
            <HelpOutline fontSize="small"/>
          </Tooltip>
        </Grid>
        <Grid item xs={3}>
          {conditionFieldPicker(field, index)}
        </Grid>
        <Grid item xs={2}>
          {conditionOperatorPicker(field, operation, index)}
        </Grid>
        <Grid item xs={5}>
          {conditionValueEditor(field, operation, value, index)}
        </Grid>
      </Grid>
    )

  }
  const getConditionDialog = () => {
    return <Dialog key={id + "-dialog"} open={conditionDialog.open} onClose={closeDialog} fullWidth={true} maxWidth="md">
            <DialogContent>
              <h5 className="card-title font-weight-bold font-size-md">{`Currently Configured Conditions for rule (${name})`}</h5>
              <DialogContentText> This rule is used when the given meeting meets the following conditions:</DialogContentText>
              { conditionDialog.conditions.map((c, i) => conditionRowRender(c, i))}
            </DialogContent>
            <DialogActions>
              <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
                <Grid item >
                  <Button onClick={onAddClick} size="medium" className="ml-3" variant="contained" color="primary">Add Condition</Button>
                </Grid>
                <Grid item className="mt-3 mb-3">
                  <Button onClick={onApplyClick} disabled={ !conditionDialog.changed } size="medium" variant="contained" color="primary">Apply Changes</Button>
                  <Button onClick={closeDialog} size="medium" variant="outlined" className="ml-3 mr-3" color="primary">Cancel</Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>
  }

  const getConditionButton = () => {
    const buttonText = conditions ? `(${conditions.length})` : "(0)";
    const buttonVariant = conditions && conditions.length ? "contained" : "outlined";
    const buttonTooltip = conditions && conditions.length ? "Click to configure conditions (view, edit, add, delete)" : "Click to add new condition";
    return <Tooltip key={id + "-tooltip"} title={buttonTooltip} className="mr-2">
      <Button key={id + "-button"} component="span" disabled={disabled} onClick={ openDialog } size="medium" variant={buttonVariant} color="secondary"><SettingsApplications/>{buttonText}</Button>
    </Tooltip>
  }


  if(!conditionDialog.open || disabled) return getConditionButton();

  return [
      getConditionButton(),
      getConditionDialog()
  ]
}
  
ConditionEditor.defaultProps = {
  id: "default",
  disabled: true, 
  name: "Default Rule Name",
  conditions: null,
  updateConditions: () => console.log("Default updateConditions")
};

export default ConditionEditor;